import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../src/app/store";
import {
  fetchUsersWithUserIds,
  fetchUsersWithEmailIds,
} from "../logic/thunk";
import DocumentTooltip from "../../documentManagement/components/DocumentTooltip";
import { getUserByEmailId, getUserById, getUsersByEmailIds, getUsersByIds } from "../logic/slice";
import { shallowEqual } from "react-redux";
import { X } from "lucide-react";
import { InlineLoadingSpinner } from "../../../components/ui/spinners/InlineLoadingSpinner";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";
import TooltipSelect from "../../../../src/components/ui/selectdropdown/v2/TooltipSelect";

interface UsersProps {
  userIds?: string[];
  emailIds?: string[];
  maxVisible?: number;
  addEnabled?: boolean;
  deleteEnabled?: boolean;
  onChange?: (ids: string[]) => void;
  email_id_options?: string[];
  user_id_options?: string[];
}

export const Users: React.FC<UsersProps> = ({
  userIds,
  emailIds,
  maxVisible = 1,
  deleteEnabled = true,
  addEnabled = true,
  user_id_options,
  email_id_options,
  onChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const isUserIdMode = !!userIds;
  const currentIds = isUserIdMode ? userIds ?? [] : emailIds ?? [];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);

        const promises = [];

        if (userIds?.length) {
          promises.push(
            dispatch(fetchUsersWithUserIds(userIds)).unwrap()
          );
        }

        if (emailIds?.length) {
          promises.push(
            dispatch(fetchUsersWithEmailIds(emailIds)).unwrap()
          );
        }

        if (user_id_options?.length) {
          promises.push(
            dispatch(fetchUsersWithUserIds(user_id_options)).unwrap()
          );
        }

        if (email_id_options?.length) {
          promises.push(
            dispatch(fetchUsersWithEmailIds(email_id_options)).unwrap()
          );
        }

        await Promise.all(promises);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [
    dispatch,
    userIds,
    emailIds,
    user_id_options,
    email_id_options,
  ]);

  const users = useAppSelector((state) => {
    if (userIds?.length) {
      return getUsersByIds(state, userIds)
    }

    if (emailIds?.length) {
      return getUsersByEmailIds(state, emailIds)
    }

    return [];
  }, shallowEqual);

  const { visibleUsers, hiddenUsers } = useMemo(() => {
    const visible = Object.values(users).slice(0, maxVisible);
    const hidden = Object.values(users).slice(maxVisible);

    return {
      visibleUsers: visible,
      hiddenUsers: hidden,
    };
  }, [users, maxVisible]);

  const dropdownOptions = useAppSelector((state) => {
    if (user_id_options?.length) {
      return user_id_options
        .filter((id) => !userIds?.includes(id))
        .map((id) => {
          const user = getUserById(state, id);
          return {
            name: user?.user_name || user?.email_id || id,
            value: id,
          };
        });
    }

    if (email_id_options?.length) {
      return email_id_options
        .filter((email) => !emailIds?.includes(email))
        .map((email) => {
          const user = getUserByEmailId(email)(state);
          return {
            name: user?.user_name || email,
            value: email,
          };
        });
    }

    return [];
  });

  const onDelete = (id_to_delete: string) => {
    const updated = currentIds.filter((id) => id !== id_to_delete);
    onChange?.(updated);
  }

  if (isLoading) {
    return (
      <InlineLoadingSpinner subtitle={RETRIEVING_DATA_SUBTITLE} />
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {visibleUsers.map((user) => (
        <div
          key={user.user_id}
          className="flex items-center gap-1 px-2 py-1 rounded-[8px] bg-[#EBEEFE] border border-transparent text-xs font-medium text-[#4B5563] leading-4"
        >
          {user.user_name || user.email_id || "N/A"}

          {deleteEnabled && (
            <X
              size={12}
              className="cursor-pointer text-[#6B7280] hover:text-red-500"
              onClick={() => {onDelete(isUserIdMode? user.user_id: user.email_id )}}
            />
          )}
        </div>
      ))}

      {hiddenUsers.length > 0 && (
        <DocumentTooltip
          content={
            hiddenUsers
            .map((user) => user?.user_name || user?.email_id || "N/A")
            .filter(Boolean)
            .join(", ")
          }
        >
          <div
            className="
              px-2 py-1
              rounded-[8px]
              bg-[#EBEEFE]
              border-1 border-transparent
              text-xs
              font-medium
              text-[#4B5563]
              leading-4
            "
          >
            +{hiddenUsers.length}
          </div>
        </DocumentTooltip>
      )}

      {addEnabled && dropdownOptions.length > 0 && (
        <TooltipSelect
          value={undefined}
          onSelectionChange={(val) => {
            onChange?.([...currentIds, val]);
          }}
          options={dropdownOptions}
          label="+ Add"
          rootContainerClassName="ml-2"
          dropdownClassName="w-[250px]"
          enableSearch={true}
          disabled={dropdownOptions.length===0}
        />
      )}
    </div>
  );
};
